"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import Magnetic from "./ui/Magnetic";

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/projects", label: "Projects" },
  { href: "/architecture", label: "Architecture" },
  { href: "/playground", label: "Lab" },
  { href: "/blog", label: "Blog" }
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const openChatwoot = () => {
    if (typeof window === "undefined") return;

    const sdk = (window as Window & {
      chatwootSDK?: {
        toggle?: () => void;
        open?: () => void;
      };
    }).chatwootSDK;

    if (sdk?.toggle) {
      sdk.toggle();
      return;
    }

    if (sdk?.open) {
      sdk.open();
    }
  };
  const [activeSection, setActiveSection] = useState(() => {
    if (typeof window === "undefined") return "home";
    const path = window.location.pathname;
    if (path.startsWith("/blog")) return "blog";
    if (path.startsWith("/about")) return "about";
    if (path.startsWith("/projects")) return "projects";
    if (path.startsWith("/architecture")) return "architecture";
    if (path.startsWith("/playground")) return "playground";
    return "home";
  });

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const path = window.location.pathname;
    if (path !== "/" && path !== "") return;

    const sections = document.querySelectorAll("section[id]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-30% 0px -70% 0px" }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 pt-5 transition-all duration-300 ${scrolled ? "bg-transparent" : "bg-transparent"
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className={`flex items-center justify-between px-4 sm:px-5 py-2.5 rounded-2xl transition-all duration-500 border ${scrolled
          ? "border-white/10 bg-black/35 backdrop-blur-2xl supports-[backdrop-filter]:bg-black/25 shadow-2xl shadow-black/40"
          : "bg-transparent border-transparent"
          }`}>
          {/* Logo */}
          <Magnetic>
            <Link href="/" className="flex items-center gap-3 group relative z-10">
              <div className="relative w-9 h-9 transition-transform duration-500 group-hover:scale-110">
                <Image src="/logo.svg" alt="Logo" fill className="object-contain logo-glow animate-pulse" />
              </div>
              <span className="text-white font-black text-lg tracking-tighter hidden sm:block">
                AMRIT<span className="text-indigo-400">.</span>
              </span>
            </Link>
          </Magnetic>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1.5 bg-white/5 rounded-full p-1 border border-white/5">
            {navLinks.map((link) => {
              const linkKey = link.href.replace(/^\/?#/, "").replace(/^\//, "");
              const isActive = activeSection === linkKey || (linkKey === "" && activeSection === "home") || (linkKey === "blog" && activeSection === "blog");

              return (
                <Magnetic key={link.href}>
                  <Link
                    href={link.href}
                    className={`px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-[0.18em] transition-all duration-300 relative ${isActive
                      ? "text-white bg-white/10"
                      : "text-gray-300 hover:text-white"
                      }`}
                  >
                    {link.label}
                  </Link>
                </Magnetic>
              );
            })}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <Magnetic>
              <button
                onClick={() => {
                  const event = new CustomEvent("open-command-palette");
                  window.dispatchEvent(event);
                }}
                className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:border-indigo-400 transition-all cursor-pointer"
                title="Press / to open Command Palette"
              >
                <i className="fi fi-rr-terminal text-sm" />
              </button>
            </Magnetic>
            <Magnetic>
              <a
                href="/profile.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-300 hover:text-white transition-colors px-3 py-2"
              >
                Resume
              </a>
            </Magnetic>
            <Magnetic>
              <button
                type="button"
                onClick={openChatwoot}
                className="btn-gradient px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-[0.18em] shadow-xl shadow-indigo-500/20 text-white"
              >
                Let&apos;s Talk
              </button>
            </Magnetic>
          </div>

          {/* Mobile Menu Btn */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle navigation menu"
            className="md:hidden w-9 h-9 rounded-xl glass border border-white/10 flex items-center justify-center text-white"
          >
            <i className={`fi ${mobileOpen ? "fi-rr-cross" : "fi-rr-menu-burger"} text-sm`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="absolute top-full left-4 right-4 mt-3 md:hidden"
          >
            <div className="glass rounded-[2rem] border border-white/10 p-6 shadow-2xl">
              <div className="flex flex-col gap-5">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="text-xl font-black text-white hover:text-indigo-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="h-px bg-white/10 my-4" />
                <a
                  href="/profile.html"
                  onClick={() => setMobileOpen(false)}
                  className="text-base font-bold text-gray-300 hover:text-white"
                >
                  View Full Resume
                </a>
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    openChatwoot();
                  }}
                  className="btn-gradient text-center py-3.5 rounded-2xl text-[11px] font-black uppercase tracking-[0.18em] text-white"
                >
                  Let&apos;s Talk
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
